import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Gift, Star, DollarSign, Users, Briefcase, Globe, 
  CheckCircle2, ArrowRight, Sparkles, Trophy, Zap,
  Palette, Code, Search, BarChart3, MessageSquare,
  ChevronRight, ExternalLink, Camera, FileText, Mail
} from 'lucide-react';

import { toast } from 'sonner';
import { API_URL } from '../config';

const API = API_URL;

export default function AffiliatePortfolioLanding() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/affiliate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          name,
          source: 'portfolio-landing',
          interested_in_portfolio: true
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Application submitted! We\'ll be in touch soon.');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const portfolioFeatures = [
    { icon: Palette, title: 'Custom Design', desc: 'Unique design tailored to your personal brand' },
    { icon: Camera, title: 'Professional Photos', desc: 'Guidance on headshots & imagery that convert' },
    { icon: FileText, title: 'Content Strategy', desc: 'We help write your bio, services & case studies' },
    { icon: Search, title: 'SEO Optimized', desc: 'Rank for your name and target keywords' },
    { icon: Globe, title: 'Custom Domain', desc: 'yourname.com or yourname.pro included' },
    { icon: Mail, title: 'Contact Forms', desc: 'Lead capture forms that convert visitors' },
  ];

  const successStories = [
    {
      name: 'Sierra Scala',
      role: 'Social Media Marketing Specialist',
      image: 'https://customer-assets.emergentagent.com/job_adsense-enabled/artifacts/jko0hbz9_image.png',
      website: 'sierrascala.com',
      quote: 'My portfolio website helped me land 3 new clients in the first month. The professional design really sets me apart from other marketers.',
      stats: { leads: '12+', clients: '3', growth: '300%' }
    }
  ];

  return (
    <>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        
        {/* HERO SECTION */}
        <section className="relative py-20 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <Badge className="mb-6 bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-4 py-2 text-sm font-bold">
                <Gift className="w-4 h-4 mr-2" />
                Limited Time: FREE $2,500 Portfolio Website for Top Affiliates
              </Badge>
              
              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
                Earn Commissions.<br />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Get Your Own Portfolio Website.
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Join our affiliate program and earn <span className="text-green-400 font-bold">20% recurring commissions</span>. 
                Refer just 5 clients and we'll build you a stunning portfolio website — <span className="text-amber-400 font-bold">completely FREE</span>.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white px-8 py-6 text-lg">
                  <a href="#apply" className="flex items-center">
                    Apply Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                  <a href="#portfolio-preview" className="flex items-center">
                    See Portfolio Examples
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>No upfront costs</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>20% recurring commissions</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>Free portfolio after 5 referrals</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 bg-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                How to Earn Your Free Portfolio
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Simple steps to start earning commissions and unlock your custom website
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '1', icon: Users, title: 'Sign Up', desc: 'Apply for our affiliate program — instant approval for qualified applicants' },
                { step: '2', icon: MessageSquare, title: 'Share', desc: 'Share your unique link with your network, clients, and social media followers' },
                { step: '3', icon: DollarSign, title: 'Earn', desc: 'Earn 20% commission on every sale, paid monthly to your account' },
                { step: '4', icon: Trophy, title: 'Unlock', desc: 'Refer 5 paying clients and we build your FREE portfolio website' },
              ].map((item, i) => (
                <div key={i} className="relative">
                  <Card className="bg-slate-700/50 border-slate-600 h-full hover:bg-slate-700 transition-colors">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white font-bold text-lg">{item.step}</span>
                      </div>
                      <item.icon className="h-8 w-8 text-blue-400 mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </CardContent>
                  </Card>
                  {i < 3 && (
                    <ChevronRight className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 h-8 w-8 text-slate-600" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PORTFOLIO PREVIEW */}
        <section id="portfolio-preview" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-500/20 text-amber-400 border-amber-500/30">
                <Star className="w-4 h-4 mr-1 fill-amber-400" />
                Real Example
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                This Could Be Your Website
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Here's an example of a portfolio website we built. Yours will be custom-designed to match your personal brand.
              </p>
            </div>
            
            {/* Portfolio Showcase */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Screenshot/Preview */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl" />
                <Card className="relative bg-slate-800 border-slate-700 overflow-hidden">
                  <div className="bg-slate-900 px-4 py-2 flex items-center gap-2 border-b border-slate-700">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 bg-slate-700 rounded px-3 py-1 text-xs text-slate-400 text-center">
                      sierrascala.com
                    </div>
                  </div>
                  <CardContent className="p-0">
                    <img 
                      src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
                      alt="Portfolio website preview"
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-2xl font-bold text-white mb-2">Sierra Scala</h3>
                      <p className="text-slate-300 mb-4">Social Media Marketing & Google PPC Specialist</p>
                      <a 
                        href="https://sierrascala.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-400 hover:text-blue-300"
                      >
                        Visit Live Site
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Features List */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white mb-6">
                  What's Included in Your Portfolio
                </h3>
                
                <div className="grid gap-4">
                  {portfolioFeatures.map((feature, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{feature.title}</h4>
                        <p className="text-sm text-slate-400">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-amber-400" />
                    <span className="font-bold text-amber-400">Total Value: $2,500+</span>
                  </div>
                  <p className="text-slate-300 text-sm">
                    This is the same quality portfolio we charge clients $2,500+ to build. 
                    You get it <span className="font-bold text-amber-400">FREE</span> after just 5 referrals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* COMMISSION STRUCTURE */}
        <section className="py-20 bg-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Earn While You Build Your Brand
              </h2>
              <p className="text-slate-400 text-lg">
                Generous commissions + incredible rewards
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Commission Card */}
              <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700/50">
                <CardContent className="p-8 text-center">
                  <DollarSign className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-4xl font-black text-green-400 mb-2">20%</h3>
                  <p className="text-lg font-semibold text-white mb-2">Recurring Commission</p>
                  <p className="text-slate-400 text-sm">
                    Earn 20% of every payment, every month, for as long as your referral stays a customer
                  </p>
                </CardContent>
              </Card>
              
              {/* Example Earnings */}
              <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700/50">
                <CardContent className="p-8 text-center">
                  <BarChart3 className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-4xl font-black text-blue-400 mb-2">$360</h3>
                  <p className="text-lg font-semibold text-white mb-2">Per Referral / Year</p>
                  <p className="text-slate-400 text-sm">
                    Average client pays $149/mo. That's $29.80/mo or $357.60/year per referral in your pocket
                  </p>
                </CardContent>
              </Card>
              
              {/* Portfolio Bonus */}
              <Card className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 border-amber-700/50">
                <CardContent className="p-8 text-center">
                  <Trophy className="h-12 w-12 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-4xl font-black text-amber-400 mb-2">FREE</h3>
                  <p className="text-lg font-semibold text-white mb-2">Portfolio Website</p>
                  <p className="text-slate-400 text-sm">
                    Refer 5 paying clients and unlock your own custom portfolio website worth $2,500+
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Earnings Calculator Teaser */}
            <div className="mt-12 text-center">
              <p className="text-slate-400 mb-4">
                <span className="text-white font-semibold">Quick Math:</span> Refer 10 clients = $300/month passive income + FREE portfolio
              </p>
            </div>
          </div>
        </section>

        {/* SUCCESS STORY */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Success Story
              </h2>
            </div>
            
            {successStories.map((story, i) => (
              <Card key={i} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <img 
                      src={story.image}
                      alt={story.name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-blue-500"
                    />
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center justify-center md:justify-start gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                      <blockquote className="text-xl text-white mb-4 italic">
                        "{story.quote}"
                      </blockquote>
                      <div className="mb-4">
                        <p className="font-bold text-white">{story.name}</p>
                        <p className="text-slate-400">{story.role}</p>
                        <a 
                          href={`https://${story.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          {story.website} →
                        </a>
                      </div>
                      <div className="flex justify-center md:justify-start gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-400">{story.stats.leads}</p>
                          <p className="text-xs text-slate-400">New Leads</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">{story.stats.clients}</p>
                          <p className="text-xs text-slate-400">New Clients</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-400">{story.stats.growth}</p>
                          <p className="text-xs text-slate-400">Visibility</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* WHO THIS IS FOR */}
        <section className="py-20 bg-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Perfect For
              </h2>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Briefcase, title: 'Freelancers', desc: 'Designers, developers, marketers, writers' },
                { icon: Users, title: 'Consultants', desc: 'Business, marketing, or tech consultants' },
                { icon: Camera, title: 'Creatives', desc: 'Photographers, videographers, artists' },
                { icon: BarChart3, title: 'Professionals', desc: 'Realtors, coaches, speakers, trainers' },
              ].map((item, i) => (
                <Card key={i} className="bg-slate-700/50 border-slate-600 text-center">
                  <CardContent className="p-6">
                    <item.icon className="h-10 w-10 text-blue-400 mx-auto mb-4" />
                    <h3 className="font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* APPLICATION FORM */}
        <section id="apply" className="py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
                    <Zap className="w-4 h-4 mr-1" />
                    Instant Approval
                  </Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Apply to Become an Affiliate
                  </h2>
                  <p className="text-slate-400">
                    Start earning commissions and work toward your free portfolio
                  </p>
                </div>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Application Received!</h3>
                    <p className="text-slate-400">
                      We'll review your application and send your affiliate link within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Your Name
                      </label>
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="John Smith"
                        className="bg-slate-700 border-slate-600 text-white"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="john@example.com"
                        className="bg-slate-700 border-slate-600 text-white"
                        required
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white py-6 text-lg"
                      disabled={loading}
                    >
                      {loading ? 'Submitting...' : 'Apply Now — It\'s Free'}
                    </Button>
                    
                    <p className="text-center text-xs text-slate-500">
                      By applying, you agree to our affiliate terms. No spam, unsubscribe anytime.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Build Your Brand AND Earn?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join our affiliate program today. Refer 5 clients, get a $2,500 portfolio website FREE.
            </p>
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 px-8 py-6 text-lg font-bold">
              <a href="#apply" className="flex items-center">
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </section>

      </div>
    </>
  );
}
